import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { catchError, defer, filter, map, Observable, switchMap, take, tap, throwError } from 'rxjs';

import { AuthState, decodeClaims, isDueForRefresh, isRestoring, ResourceTokens } from '@ninety/mobile/platform/auth';

import { SKIP_SESSION } from './session-http.context';
import { SessionActions } from './session.actions';
import { AUTH_CLOCK } from './session.effects';
import { selectSession } from './session.selectors';

/** Whether these tokens are inside their refresh window. Decoding is not verifying — see claims.ts. */
function isDue(tokens: ResourceTokens, clock: () => number): boolean {
  return isDueForRefresh(decodeClaims(tokens.accessToken), clock());
}

/**
 * Attaches the session to outgoing requests, and waits when there isn't one to attach yet.
 *
 * ## Why this is reactive rather than dispatch-then-read
 *
 * This is where ADR 0004's main consequence shows up. `transition()` runs in an effect, so after
 * dispatching an event the store still holds the old state — a read on the next line returns what
 * was true before. Every decision here is therefore made by waiting for a state to arrive, never
 * by dispatching and then looking. There is no `firstValueFrom` after a `dispatch` anywhere below.
 */
export function sessionInterceptorFn(req: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> {
  if (req.context.get(SKIP_SESSION)) return next(req);

  const store = inject(Store);
  const clock = inject(AUTH_CLOCK);

  return store.select(selectSession).pipe(
    take(1),
    switchMap(onArrival =>
      // Signed out before this request was ever made, so there is nothing to wait for and nothing
      // to attach. It goes out bare, which lets a call that legitimately runs while signed out
      // work without having to set SKIP_SESSION itself. A 401 on it is the Resource Server's
      // answer, not a rejected Access Token, so it must not be reported as one.
      onArrival.tag === 'unauthenticated' ? next(req) : send(req, next, store, clock)
    )
  );
}

/**
 * Wait for a session, send, and recover once from a rejected Access Token.
 *
 * ## Losing the session while queued
 *
 * A restore that ends in `unauthenticated` has already told the app to navigate to the sign-in
 * screen, so replaying the requests queued behind it would turn one failed restore into a burst
 * that is guaranteed to 401, from components that are being torn down. They error locally instead.
 *
 * It errors rather than completing empty because an empty completion is not something callers
 * handle: `firstValueFrom` throws `EmptyError`, and anything holding a loading flag never clears
 * it. An `HttpErrorResponse` travels the path every other request failure already takes.
 *
 * ## Recovering from a 401
 *
 * `rejected` carries the Access Token a previous attempt was refused with, and it does two things.
 * It bounds the recursion to a single retry — a second 401 is the answer to a token the machine
 * has just minted, and asking it to restore again is how a refresh loop starts. And it is what the
 * retry waits for: not "authorized", but "authorized on a token that is not the rejected one".
 *
 * `transition()` runs in an effect, so the store may still hold `authorized` with the rejected token
 * at the moment the retry begins; a retry that read the state right after dispatching would resend
 * exactly the token that was just refused.
 */
function send(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
  store: Store,
  clock: () => number,
  rejected?: string
): Observable<HttpEvent<unknown>> {
  return usableSession(store, clock, rejected).pipe(
    switchMap(tokens => {
      if (!tokens) return throwError(() => sessionLost(req));

      return next(withSession(req, tokens)).pipe(
        catchError(error => {
          if (rejected || !isRejection(error)) return throwError(() => error);

          store.dispatch(SessionActions.eventReceived({ event: { type: 'accessTokenRejected' } }));
          return send(req, next, store, clock, tokens.accessToken);
        })
      );
    })
  );
}

function isRejection(error: unknown): boolean {
  return error instanceof HttpErrorResponse && error.status === 401;
}

// The wording covers both ways a queued request gives up: a session that ended while it waited, and
// a restore that was deferred rather than run, where no session ended and one may yet arrive.
const NO_SESSION = 'no session was available for this request';

function sessionLost(req: HttpRequest<unknown>): HttpErrorResponse {
  const error = new HttpErrorResponse({
    status: 401,
    statusText: 'Unauthorized',
    url: req.url,
    error: NO_SESSION,
  });

  // This one is thrown here rather than returned by `next`, so it never reaches `httpErrorInterceptor`
  // and cannot inherit the toString that interceptor attaches. Without this it stringifies to
  // "[object Object]" in any caller that renders it.
  Object.defineProperty(error, 'toString', {
    value: () => NO_SESSION,
    enumerable: false,
    configurable: true,
    writable: true,
  });
  return error;
}

/** What a state means to a request that is waiting on it. */
type Verdict =
  /** Send now, on this state's Resource Tokens. */
  | 'send'
  /** There is no session coming. Fail the request without sending it. */
  | 'giveUp'
  /** The machine is still working. Stay queued. */
  | 'wait';

/**
 * Resolves once with Resource Tokens that are worth sending, or `null` when none are coming.
 *
 * Emits nothing while the machine is working — `restoringSession`, `authorizing`, `refreshing` —
 * which is what queues a request rather than failing it. An Access Token inside its refresh window
 * is not worth sending, so this asks for a refresh and keeps waiting.
 *
 * ## Asking exactly once
 *
 * `asked` and `attempted` exist for one reason: **a refresh that gives up returns the session it
 * already had.** When a network restore exhausts its attempts, `transition()` goes back to
 * `authorized` holding the same tokens — "an unreachable network is not a revoked token". Those
 * tokens are still inside the refresh window, so asking again on every `authorized` emission would
 * restore, fail, land back here and ask again: an offline device retrying forever with the request
 * never sent. Asking once and then taking what the machine offers is what terminates that.
 *
 * Sending a token with time left on it beats hanging, and if the Resource Server disagrees the 401
 * path below handles it.
 *
 * The ask itself is a plain dispatch with no follow-up read, and it is not guarded across
 * requests. Ten requests arriving at once produce ten `refreshRequested` events, of which the machine
 * acts on the first and coalesces the rest — that guard belongs in `transition()`, where it is one
 * rule instead of one per caller.
 */
function usableSession(store: Store, clock: () => number, rejected?: string): Observable<ResourceTokens | null> {
  return defer(() => {
    // A 401 retry has already asked, by dispatching `accessTokenRejected`.
    let asked = rejected !== undefined;
    let attempted = false;

    return store.select(selectSession).pipe(
      tap(state => {
        if (state.tag !== 'authorized') {
          // The machine left `authorized`, so the refresh we asked for is genuinely under way.
          if (asked) attempted = true;
          return;
        }
        if (!asked && isDue(state.tokens, clock)) {
          asked = true;
          store.dispatch(
            SessionActions.eventReceived({ event: { type: 'refreshRequested', trigger: 'preflightSkew' } })
          );
        }
      }),
      map(state => [state, verdictOn(state, clock, rejected, attempted)] as const),
      filter(([, verdict]) => verdict !== 'wait'),
      take(1),
      map(([state, verdict]) => (verdict === 'send' && state.tag === 'authorized' ? state.tokens : null))
    );
  });
}

/**
 * What to do with a state, given what this request has already tried.
 *
 * `attempted` means the machine has been away and come back, so whatever it is offering now is its
 * final answer — take it rather than asking for better. The one thing not worth taking is the
 * Access Token a previous attempt was already refused with: resending that is a guaranteed 401.
 */
function verdictOn(state: AuthState, clock: () => number, rejected: string | undefined, attempted: boolean): Verdict {
  // Waiting is only worth doing while something is working towards an answer, and two states are.
  // A Grant is one: it ends in `authorized` or `unauthenticated`, so a request queued behind one is
  // answered either way. It is named here rather than folded into `isRestoring`, which answers the
  // narrower "is a Session Restore running?" — the route guards read that one and need it narrow,
  // or the sign-in screen's own guard would hang on the Grant it is driving.
  if (state.tag === 'authorizing') return 'wait';
  // `isRestoring` covers the other: a launch, whose every phase is waiting on a Command. A replay is
  // deliberately not one — it lives in `unauthenticated`, which never reaches this function at all,
  // because a request made from the sign-in screen goes out bare rather than queueing behind a prompt
  // the person may dismiss.
  if (state.tag !== 'authorized') return isRestoring(state) ? 'wait' : 'giveUp';
  if (state.tokens.accessToken === rejected) return attempted ? 'giveUp' : 'wait';
  if (attempted) return 'send';
  return isDue(state.tokens, clock) ? 'wait' : 'send';
}

function withSession(req: HttpRequest<unknown>, tokens: ResourceTokens): HttpRequest<unknown> {
  return req.clone({
    setHeaders: { Authorization: `Bearer ${tokens.accessToken}`, Entitlements: tokens.entitlementsToken },
  });
}
